import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { Card } from '@sorare/core/src/components/card/Card';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { DumbNotification } from 'components/activity/DumbNotification';

import { commonNotificationInterfaceFragment } from '../fragments';
import type { CommonNotificationProps } from '../types';
import { Rewards } from './Rewards';
import type { AuctionNotification_auctionNotification } from './__generated__/index.graphql';
import { useAuctionLink } from './useAuctionLink';

type Props = CommonNotificationProps & {
  notification: AuctionNotification_auctionNotification;
};

export const AuctionNotification = ({ notification, ...rest }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { name, createdAt, tokenAuction, rewards, sport, read } = notification;
  const makeAuctionLink = useAuctionLink();

  const link = makeAuctionLink(tokenAuction);

  const titleNode = (
    <>
      <LazyMarkdown data={notification.markdownBody} />
      {name === 'card_bought' && <Rewards rewards={rewards} />}
    </>
  );

  return (
    <DumbNotification
      name={name}
      title={titleNode}
      userAvatar={currentUser}
      cardPicture={<Card card={tokenAuction.cards[0]} />}
      link={link}
      createdAt={createdAt}
      sport={sport}
      read={read}
      {...rest}
    />
  );
};

AuctionNotification.fragments = {
  auctionNotification: gql`
    fragment AuctionNotification_auctionNotification on AuctionNotification {
      id
      ...Notification_notificationInterface
      tokenAuction {
        id
        ...AuctionLink_auction
        cards: anyCards {
          slug
          sport
          ...Card_anyCard
        }
      }
      rewards {
        id
        ...Rewards_anyRewardInterface
      }
    }
    ${useAuctionLink.fragments.auctionLink}
    ${commonNotificationInterfaceFragment}
    ${Card.fragments.anyCard}
    ${Rewards.fragments.anyReward}
  ` as TypedDocumentNode<AuctionNotification_auctionNotification>,
};
